'use client';

import React, { useEffect, useRef } from 'react';

const problems = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
      </svg>
    ),
    label: '#1',
    title: 'Você fez o serviço, recebeu — e nunca mais entrou em contato com esse cliente.',
    description: 'Sem um contato, o cliente esquece que precisa de manutenção. Quando lembra, liga para o concorrente. E você nem sabe que o perdeu.',
    color: 'from-red-500/20 to-transparent',
    borderColor: 'border-red-500/30',
    textColor: 'text-red-400',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
      </svg>
    ),
    label: '#2',
    title: 'Receita recorrente sendo desperdiçada todo mês.',
    description: 'Cada cliente esquecido é uma manutenção que não aconteceu. Multiplique isso por 50, 100, 200 clientes — e veja quanto dinheiro está saindo pelo ralo.',
    color: 'from-orange-500/15 to-transparent',
    borderColor: 'border-orange-500/25',
    textColor: 'text-orange-400',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
      </svg>
    ),
    label: '#3',
    title: 'Clientes antigos ligam — em geral quando lembram ou quando dá problema no ar.',
    description:
      'Captar novos clientes ainda custa muito mais do que vender de novo. Só que esse “de novo” costuma vir no ritmo deles, não no ciclo certo da manutenção — e muita revisão de rotina passa batida.',
    color: 'from-yellow-500/15 to-transparent',
    borderColor: 'border-yellow-500/25',
    textColor: 'text-yellow-400',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
    label: '#4',
    title: 'Sem controle, você não sabe quem precisa de manutenção agora.',
    description: 'Sem organização de datas, você age no escuro. Não tem como prever a agenda, nem garantir receita mensal estável. Tudo depende da sorte.',
    color: 'from-purple-500/15 to-transparent',
    borderColor: 'border-purple-500/25',
    textColor: 'text-purple-400',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
      </svg>
    ),
    label: '#5',
    title: 'Quem está na hora de avisar? Na mão, isso se perde na correria do dia a dia.',
    description:
      'Cabeça, papel e planilha não te cutucam na hora certa — e cada semana sem revisão vira cliente que passa da data sem você perceber.',
    color: 'from-blue-500/15 to-transparent',
    borderColor: 'border-blue-500/25',
    textColor: 'text-blue-400',
  },
];

const ProblemSection: React.FC = () => {
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const delay = Number(el.dataset.delay || 0);
            setTimeout(() => {
              el.classList.remove('reveal-hidden');
              el.classList.add('reveal-visible');
            }, delay);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="problema" className="section-padding bg-off-white">
      <div className="max-w-[1320px] mx-auto">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <span className="tag-badge bg-red-100 text-red-600 border border-red-200 mb-5 inline-block">
            O Problema Real
          </span>
          <h2 className="font-fraunces text-navy text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.1] mb-6">
            Você está deixando
            <span className="italic text-red-500"> dinheiro na mesa</span>
            <br />todo mês.
          </h2>
          <p className="text-text-muted text-lg font-light leading-relaxed">
            Cada cliente que você não lembra de contatar é uma manutenção que vai para o concorrente — ou simplesmente não acontece. Reconhece alguma dessas situações?
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {problems.map((problem, i) => (
            <div
              key={i}
              ref={(el) => { cardsRef.current[i] = el; }}
              data-delay={i * 120}
              className={`reveal-hidden bento-card rounded-2xl p-7 bg-white border ${problem.borderColor} bg-gradient-to-br ${problem.color} ${
                i === 0 ? 'md:col-span-2 lg:col-span-2' : ''
              } ${
                i === 3 ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
            >
              <div className={`${problem.textColor} mb-4`}>
                {problem.icon}
              </div>
              <span className={`text-xs font-bold uppercase tracking-widest ${problem.textColor} mb-3 block`}>
                {problem.label}
              </span>
              <h3 className="font-fraunces text-navy text-xl md:text-2xl font-medium leading-snug mb-3">
                {problem.title}
              </h3>
              <p className="text-text-muted text-sm leading-relaxed">
                {problem.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom callout */}
        {/* <div
          className="mt-10 p-7 rounded-2xl bg-navy text-white flex flex-col md:flex-row items-center justify-between gap-6 reveal-hidden"
          ref={(el) => { cardsRef.current[5] = el; }}
          data-delay="600"
        >
          <div>
            <p className="font-fraunces text-2xl md:text-3xl font-light mb-1">
              A solução não é trabalhar mais.
            </p>
            <p className="text-white/60 font-light">
              É ter um sistema que te avisa na hora certa — para você entrar em contato com o cliente certo.
            </p>
          </div>
          <a
            href="#como-funciona"
            className="btn-primary flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-bold sm:px-7 sm:py-3.5"
          >
            Ver como funciona
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div> */}
      </div>
    </section>
  );
};

export default ProblemSection;